<template>
    <div>
        <h1 class="page-title">{{ $t("catalogue.productRestrictions.title") }}</h1>
    </div>
    <div class="card">
        <DataTable
            :first="(currentPage - 1) * rows"
            :value="tableData"
            :rows="rows"
            :rowHover="true"
            :paginator="true"
            :loading="isLoading"
            :rowsPerPageOptions="rowsPerPageOptions"
            lazy
            @page="onPaginate($event)"
            :currentPage="currentPage"
            :totalRecords="totalRecords"
        >
            <template #header>
                <div class="search-container">
                    <span class="p-input-icon-left">
                    <i class="pi pi-search" />
                    <InputText
                        v-model="criteria"
                        @keyup.enter="onFilterDt"
                        @input="onClearInputFilter"
                        placeholder="Rechercher un produit"/>
                    </span>
                    <router-link
                        class="group inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 bg-blue-600 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus-visible:outline-blue-600"
                        :to="`/admin/companies/${router.currentRoute.value.params.uuid}/hidden-master-products/add`"
                    >
                    Cacher un produit
                    </router-link>
                </div>
            </template>
            <div class="card-body pt-0">
                <Column field="product_name" header="Nom du produit"/>        
                <Column field="brand" header="Marque" />
                <Column field="supplier_name" header="Fournisseur"/>
                <Column headerStyle="width: 12em;">
                    <template #header>
                        <div>Actions</div>
                    </template>
                    <template #body="slotProps">
                        <Button 
                            @click="() => deleteProductRestriction(slotProps.data.company_uuid, slotProps.data.master_product_uuid)" 
                            type="button" 
                            title="Supprimer" 
                            icon="pi pi-trash" 
                            class="ml-1 p-button-danger">
                        </Button>
                    </template>
                </Column>
            </div>
            <template #empty>Aucun résultat trouvé.</template>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import DataTable from "primevue/datatable";
import { ref, onMounted } from "vue";
import { productService } from "@/_services/product/product";
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import { IMasterProduct } from "@/models/product/IMasterProduct";
import router from "@/router";

const page = ref<number>(1);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const rows = ref<number>(10);
const isLoading = ref<boolean>(true);
const masterProducts = Array<IMasterProduct>();
const tableData = ref<Array<IMasterProduct>>(masterProducts);
const currentPage = ref<number>(1);
const criteria = ref<string>("");                                                                                                                                    
const totalRecords = ref<number>(0);

onMounted(async () => {
    await populateDatatable(rows.value, page.value, criteria.value);
});

const populateDatatable = async (itemsPerPage: number, page: number, criteria: string) => {
    showSpinner();

    const companyUuid = router.currentRoute.value.params.uuid;
    if (!companyUuid) {
        swal.error("Aucun identifiant de société trouvé dans l'URL.");
        return;
    }

    const [loadingDt, totalRecordsDt, tableDataDt] = await productService.fetchProductRestrictionsByCompany(
        companyUuid,
        itemsPerPage,
        page,
        criteria
    );
    totalRecords.value = totalRecordsDt;
    isLoading.value = loadingDt;
    tableData.value = tableDataDt;
};


const onPaginate = async (e) => {
    rows.value = e.rows;
    currentPage.value = e.page + 1;
    page.value = e.page + 1;
    populateDatatable(rows.value, currentPage.value , criteria.value);
};

const onFilterDt = async () => {
    if (criteria.value != "") {
        await populateDatatable(rows.value, page.value, criteria.value);
    }
};

const onClearInputFilter = async () => {
    if (criteria.value === "") {
        await populateDatatable(rows.value, page.value, "");
    }
};

const deleteProductRestriction = async (companyUuid: string, masterProductUuid: string) => {
    swal
    .confirm("Êtes-vous sûr de vouloir supprimer la restriction de visibilité du produit ?")
    .then((result) => {
        if (result.isConfirmed) {
            ApiService.delete(`/admin/master-product-company/${companyUuid}/${masterProductUuid}`).then(() => {
                swal.success(
                    'Supprimé !',
                    'Vous venez de supprimer la restriction.'
                );
                populateDatatable(rows.value, page.value, criteria.value);
            });
        }
    });
}

const showSpinner = () => {
    isLoading.value = true;
}
</script>

<style scoped lang="scss">
@import "primevue/resources/themes/tailwind-light/theme.css";
@import "primevue/resources/primevue.min.css";
@import "primeicons/primeicons.css";

select {
    @apply font-normal;
}
.card-body {
    @apply bg-white
}
.search-container {
    display: flex;
    justify-content: space-between;
}
</style>